
/* eslint-disable*/
import { CashOnCashExport } from 'src/app/domain/cashflow/dto/CashOnCashExport';
import { CashFlowMatrixDTO } from 'src/app/domain/cashflow/dto/CashFlowMatrixDTO';
import { getCell, formats, withCellStyles, getStatusForSubTotal } from '../../utils';
import type { CashOnCashHeaders, CompaniesByRealization } from './interfaces';
import { CellStyle } from 'xlsx-js-style/types';
import { Rows } from '../../interfaces';
import { intToExcelCol } from 'excel-column-name';
import { centeredBold, centeredBoldGrayForHeaders, leftBold, leftBoldGrayForHeaders, centeredBoldLightGrayBg }
  from 'src/app/util/excelStyles';

/**
 * Get common data mapping to start with (for main and totals).
 *
 * @param cashFlowListLength length of company's cashflow.
 * @param i row index.
 * @param firstFlowKey excel key letter of the first non-zero value in company cashflow list
 * @param mappedObj company or total for mapping
 * @param withDue
 * @param withReceivables
 * @param isTotal
 */

export const getStartingDataMapping = (cashFlowListLength: number, i: number, firstFlowKey: string,
  mappedObj: CashFlowMatrixDTO, withDue: boolean, withReceivables: boolean, isTotal: boolean): any => {
  let additionalIndexes = withDue ? 1 : 0;
  additionalIndexes += withReceivables ? 1 : 0;
  const unrValueIndex = cashFlowListLength + additionalIndexes + 2;
  const lastFlowKey = intToExcelCol(cashFlowListLength + additionalIndexes);
  const unrValueKey = intToExcelCol(unrValueIndex);
  const dueLetterKey = intToExcelCol(unrValueIndex - 1);
  const moicKey = intToExcelCol(unrValueIndex + 3);
  const flowKeyDiff = `B${i}:${lastFlowKey}${i}`;
  const gainLossLetterKey = intToExcelCol(unrValueIndex + 1);
  const irrFormula = `=IFERROR(IF(${moicKey}${i}=0,-1,IF(SUM(${dueLetterKey}${i},${unrValueKey}${i})<0,"",IF(${gainLossLetterKey}${i}>=1,XIRR(${firstFlowKey}${i}:${unrValueKey}${i},$${firstFlowKey}$1:$${unrValueKey}$1),XIRR(${firstFlowKey}${i}:${unrValueKey}${i},$${firstFlowKey}$1:$${unrValueKey}$1,-0.1)))),"")`;
  const data = {
    '': isTotal ? mappedObj.companyName : `   ${mappedObj.companyName}`,
    'Unrealized value': getCell({ v: mappedObj.fmv, z: formats.thousandsSeparator, t: 'n' }),
    'Gain/Loss': getCell({
      f: `SUM(B${i}:${unrValueKey}${i})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    IRR: getCell({
      f: irrFormula,
      z: formats.percents,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(-(${unrValueKey}${i}+SUMIF(${flowKeyDiff},">"&0,${flowKeyDiff}))/SUMIF(${flowKeyDiff},"<"&0,${flowKeyDiff}),"")`,
      z: formats.multiplier,
      t: 'n',
    }),
  };

  if (withDue) {
    data['Due to company'] = getCell({ v: zeroToNull(mappedObj.commitment), z: formats.thousandsSeparator, t: 'n' });
  }
  if (withDue) {
    data['Receivables'] = getCell({ v: zeroToNull(mappedObj.advanceInvestment), z: formats.thousandsSeparator, t: 'n' });
  }
  return data;
};

function zeroToNull(v: number) {
  if (v !== 0) {
    return v;
  }
  return undefined;
}

/**
 * Get data mapping.
 *
 * @param company CompanyIrr to map.
 * @param withDue
 * @param withReceivables
 * @param i row index.
 * @param firstFlowKey excel key letter of the first non-zero value in company cashflow list
 */
export const getNewMapping = (company: CashFlowMatrixDTO, withDue: boolean,
  withReceivables: boolean, i: number, firstFlowKey: string, type: string): any => {
  const data = getStartingDataMapping(company.cashFlowList?.length, i, firstFlowKey, company, withDue, withReceivables, false);
  if (type === 'amount') {
    investmentRowValueIndexes.push(i);
  } else {
    fmvRowValueIndexes.push(i);
  }
  company.cashFlowList?.forEach((cashflow) => {
    data[cashflow.transactionDate?.toString()] = getCell({
      v: zeroToNull(cashflow[type]),
      z: formats.thousandsSeparator,
      t: 'n',
    });
  });
  return data;
};

/**
 * Get data mapping.
 *
 * @param company CompanyIrr to map.
 * @param withDue
 * @param withReceivables
 * @param i row index.
 * @param firstFlowKey excel key letter of the first non-zero value in company cashflow list
 */

export const getNewMappingForMoic = (company: CashFlowMatrixDTO, withDue: boolean,
  withReceivables: boolean, i: number, firstFlowKey: string): any => {
  const data = getStartingDataMapping(company.cashFlowList?.length, i, firstFlowKey, company, withDue, withReceivables, false);
  company.cashFlowList?.forEach((cashflow, indx) => {
    const currentColumnLetter = intToExcelCol(indx + 2);
    const fmvRowIndex = fmvRowValueIndexes[rowIterator];
    const investmentRowIndex = investmentRowValueIndexes[rowIterator];
    data[cashflow.transactionDate?.toString()] = getCell({
      f: `IFERROR(${currentColumnLetter}${fmvRowIndex}/${currentColumnLetter}${investmentRowIndex},0)`,
      z: formats.emptyMultiplier,
      t: 'n',
    });
  });
  rowIterator++;
  return data;
};

/**
 * Split companies by realization.
 *
 * @param companies CompanyIrr list.
 * @returns split companies.
 */

export const getModelsByStatus = (companies: CashFlowMatrixDTO[]): CompaniesByRealization => companies.reduce<CompaniesByRealization>((acc, c) => {
  if (c.isRealized) {
    acc.sold.push(c);
  } else {
    acc.active.push(c);
  }
  return acc;
}, { active: [], sold: [] });

/**
 * Finds excel key of the first non-zero amount in company cashflow.
 *
 * @param company data of one company.
 * @returns excel key (letter).
 */
function findFirstFlowKey(company: CashFlowMatrixDTO): string {
  for (let i = 0; i < company.cashFlowList.length; i++) {
    const flow = company.cashFlowList[i];
    if (flow.amount !== 0) {
      const key: string = intToExcelCol(Number(i) + 2);
      firstValueArray.push(key);
      return key;
    }
  }
}

export const getManyRowsForCashOnCash = (companies: CashFlowMatrixDTO[], headers: string[],
  withDue: boolean, withReceivables: boolean, rowIndex: number, type: string): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = companies.map((d, i) => getNewMapping(d, withDue, withReceivables, i + rowIndex, findFirstFlowKey(d), type));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of [''].concat(headers)) {
      if (header !== '') {
        row.push(withCellStyles(mapped[header], { alignment: { horizontal: 'center' } }));
      } else {
        row.push(mapped[header]);
      }
    }
    rows.push(row);
  }

  return rows;
};


export const getManyRowsForMoic = (companies: CashFlowMatrixDTO[], headers: string[],
  withDue: boolean, withReceivables: boolean, rowIndex: number): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = companies.map((d, i) =>
    getNewMappingForMoic(d, withDue, withReceivables, i + rowIndex, findFirstFlowKey(d)));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of [''].concat(headers)) {
      if (header !== '') {
        row.push(withCellStyles(mapped[header], { alignment: { horizontal: 'center' } }));
      } else {
        row.push(mapped[header]);
      }
    }
    rows.push(row);
  }

  return rows;
};

export const getPreparedDataCommon = (exportExcelData: CashOnCashExport, headers: string[]): Rows => {
  const newData: Rows = [];
  const costRows = getPreparedDataForCashOnCash(exportExcelData, headers, 'amount');
  newData.push(...costRows);
  newData.push(null);
  currentRowIndex += 3;
  const fmvRows = getPreparedDataForCashOnCash(exportExcelData, headers, 'unrealized');
  currentRowIndex += 1;
  newData.push(...fmvRows);
  newData.push(null);
  currentRowIndex += 2;
  const moicRows = getPreparedDataForMoic(exportExcelData, headers);
  newData.push(...moicRows);
  refreshUtilsData();
  return newData;
};

export const getPreparedDataForCashOnCash = (exportExcelData: CashOnCashExport, headers: string[], type: string): Rows => {
  const newData: Rows = [];
  const withDue = exportExcelData.withDue;
  const withReceivables = exportExcelData.withReceivables;
  const typeOfTable = type === 'amount' ? 'Investment' : 'Fair value';
  // pushing headers with styling
  const designedHeaderRow = [];
  const updatedHeaders = [typeOfTable].concat(headers);
  updatedHeaders.forEach((header) => designedHeaderRow.push(withCellStyles(header, centeredBoldGrayForHeaders)));
  newData.push(designedHeaderRow);
  // getting adding and sold data
  const { active, sold } = getModelsByStatus(exportExcelData.mainData);
  if (active.length) {
    // pushing status row
    const activeSoldRow = getActiveSoldRow('Active', withDue, withReceivables, headers);
    newData.push(activeSoldRow);
    currentRowIndex += 1;
    // pushing active data
    const activeRows = getManyRowsForCashOnCash(active, headers, withDue, withReceivables, currentRowIndex, type);
    newData.push(...activeRows);
    currentRowIndex += active.length;
    // pushing sub-totals
    const subTotalRow = getSubTotalRowForCashOnCash('Active', headers, withDue, withReceivables, active.length, active, type);
    newData.push(...subTotalRow);
    currentRowIndex += 1;
  }
  if (sold.length) {
    // push status row
    newData.push([withCellStyles('Realized', leftBold)]);
    currentRowIndex += 1;
    const soldRows = getManyRowsForCashOnCash(sold, headers, withDue, withReceivables, currentRowIndex, type);
    // pushing sold data
    newData.push(...soldRows);
    currentRowIndex += sold.length;
    const subTotalSoldRow =
      getSubTotalRowForCashOnCash('Realized', headers, withDue, withReceivables, sold.length, sold, type);
    // pushing sub-totals
    newData.push(...subTotalSoldRow);
  }
  if (active.length && sold.length) {
    currentRowIndex += 1;
    const grandTotalRow = getGrandTotalRowForCashOnCash(headers, withDue, withReceivables);
    // pushing grand totals
    newData.push(grandTotalRow);
  }
  return newData;
};

export const getPreparedDataForMoic = (exportExcelData: CashOnCashExport, headers: string[]): Rows => {
  const newData: Rows = [];
  const withDue = exportExcelData.withDue;
  const withReceivables = exportExcelData.withReceivables;
  const headerRow = [];
  const newHeaders = ['MOIC'].concat(headers);
  newHeaders.forEach((header) => headerRow.push(withCellStyles(header, leftBoldGrayForHeaders)));
  newData.push(headerRow);
  const { active, sold } = getModelsByStatus(exportExcelData.mainData);
  if (active.length) {
    // pushing status row
    const activeSoldRow = getActiveSoldRow('Active', withDue, withReceivables, headers);
    newData.push(activeSoldRow);
    currentRowIndex += 1;
    // pushing active data
    const activeRows = getManyRowsForMoic(active, headers, withDue, withReceivables, currentRowIndex);
    newData.push(...activeRows);
    currentRowIndex += active.length;
    // pushing sub-totals
    const subTotalRow = getSubTotalRowForMoic('Active', headers, withDue, withReceivables, active.length, active);
    newData.push(...subTotalRow);
    currentRowIndex += 1;
  }
  if (sold.length) {
    // push status row
    newData.push([withCellStyles('Realized', centeredBold)]);
    currentRowIndex += 1;
    const soldRows = getManyRowsForMoic(sold, headers, withDue, withReceivables, currentRowIndex);
    // pushing sold data
    newData.push(...soldRows);
    currentRowIndex += sold.length;
    const subTotalSoldRow =
      getSubTotalRowForMoic('Realized', headers, withDue, withReceivables, sold.length, sold);
    // pushing sub-totals
    newData.push(...subTotalSoldRow);
  }
  if (active.length && sold.length) {
    currentRowIndex += 1;
    const grandTotalRow = getGrandTotalRowForMoic(headers, withDue, withReceivables);
    // pushing grand totals
    newData.push(grandTotalRow);
  }

  return newData;
};

export const getActiveSoldRow = (status: string, withDue: boolean, withReceivables: boolean, headersForLogic: string[]): Rows => {
  const rows: Rows = [];
  const row = [];
  headersForLogic = [''].concat(headersForLogic);
  const mapped = getActiveSoldRowMapping(status, withDue, withReceivables);
  for (const header of headersForLogic) {
    row.push(withCellStyles(mapped[header], centeredBold));
  }
  rows.push(row);
  return row;
};

export const getActiveSoldRowMapping = (status: string, withDue: boolean, withReceivables: boolean) => {
  const body = {};
  body[''] = status;
  if (withDue) {
    body['Due to company'] = 'Due to company';
  }
  if (withReceivables) {
    body['Receivables'] = 'Receivables';
  }
  body['Unrealized value'] = 'Unrealized value';
  return body;
};

export const getSubTotalRow = (status: string, headers: CashOnCashHeaders, withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[]): Rows => {
  const rows: Rows = [];
  const row = [];
  firstValueArray = firstValueArray.sort();

  const totalMapping = getSubTotalMapping(status, headers, withDue, withReceivables, dataLen, data);
  for (const header of headers.headersForLogic) {
    row.push(withCellStyles(totalMapping[header], centeredBoldLightGrayBg));
  }
  rows.push(row);
  firstValueArray = [];
  return rows;
};

export const getSubTotalRowForCashOnCash = (status: string, headers: string[], withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[], type: string): Rows => {
  const rows: Rows = [];
  const row = [];
  firstValueArray = firstValueArray.sort();
  if (type === 'amount') {
    subTotalIndexArrayInvestment.push(currentRowIndex);
  } else {
    subTotalIndexArrayFmv.push(currentRowIndex);
  }
  const totalMapping = getSubTotalMappingForCashOnCash(status, headers, withDue, withReceivables, dataLen, data);
  for (const header of [''].concat(headers)) {
    row.push(withCellStyles(totalMapping[header], boldLightGrayBgCenter));
  }
  rows.push(row);
  firstValueArray = [];
  return rows;
};

export const getSubTotalRowForMoic = (status: string, headers: string[], withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[]): Rows => {
  const rows: Rows = [];
  const row = [];
  firstValueArray = firstValueArray.sort();

  const totalMapping = getSubTotalMappingForMoic(status, headers, withDue, withReceivables, dataLen, data);
  for (const header of [''].concat(headers)) {
    row.push(withCellStyles(totalMapping[header], boldLightGrayBgCenter));
  }
  rows.push(row);
  firstValueArray = [];
  return rows;
};


export const getSubTotalMapping = (status: string, headers: CashOnCashHeaders, withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[]): any => {
  const total = {};
  totalIndexArray.push(currentRowIndex);
  total[''] = `Total ${getStatusForSubTotal(status)}`;
  let columnIndex: number;
  // it's unrealized column
  const stopGainLossIndexLetter = intToExcelCol(headers.headersDisplayed.length - 3);
  const startRowIndex = currentRowIndex - dataLen;
  for (const [i, date] of headers.dates.entries()) {
    columnIndex = i + 2;
    total[date] = getCell({
      f: `=IFERROR(SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1}), 0)`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withDue) {
    columnIndex += 1;
    total['Due to company'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withReceivables) {
    columnIndex += 1;
    total['Receivables'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  total['Gain/Loss'] = getCell({
    f: `SUM(B${currentRowIndex}:${stopGainLossIndexLetter}${currentRowIndex})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  total['Unrealized value'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  const indicesInHeaderDates = [];
  headers.dates.forEach((date, index) => {
    data.forEach((company) => {
      const matchingCashFlow = company.cashFlowList.find((cashFlow) =>
        cashFlow.transactionDate.toString() === date && cashFlow.amount !== 0);
      if (matchingCashFlow) {
        indicesInHeaderDates.push(index + 2);
      }
    });
  });
  const startIrrIndexLoop = indicesInHeaderDates[0];
  const firstNonEmptyIrr = intToExcelCol(startIrrIndexLoop);
  const dueColumnIndex = intToExcelCol(columnIndex - (withDue ? 1 : 0) - (withReceivables ? 1 : 0) - 1);
  const unrealizedValueColumnLetter = intToExcelCol(columnIndex - 1);
  const moicColumnLetter = intToExcelCol(columnIndex + 2);
  const unrValueKey = intToExcelCol(columnIndex - 1);
  const gainLossLetterKey = intToExcelCol(columnIndex);

  total['IRR'] = getCell({
    f: `IFERROR(IF(${moicColumnLetter}${currentRowIndex}=0,-1,IF(SUM(${dueColumnIndex}${currentRowIndex},${unrealizedValueColumnLetter}${currentRowIndex})<0,"",IF(${gainLossLetterKey}${currentRowIndex}>=1,XIRR(${firstNonEmptyIrr}${currentRowIndex}:${unrValueKey}${currentRowIndex},$${firstNonEmptyIrr}$1:$${unrValueKey}$1),XIRR(${firstNonEmptyIrr}${currentRowIndex}:${unrValueKey}${currentRowIndex},$${firstNonEmptyIrr}$1:$${unrValueKey}$1,-0.1)))), "")`,
    z: formats.percents,
    t: 'n',
  });

  total['MOIC'] = getCell({
    f: `IFERROR(-(${unrealizedValueColumnLetter}${currentRowIndex}) / SUMIF(${firstNonEmptyIrr}${currentRowIndex}:${dueColumnIndex}${currentRowIndex},"<"&0,${firstNonEmptyIrr}${currentRowIndex}:${dueColumnIndex}${currentRowIndex}),"")`,
    z: formats.multiplier,
    t: 'n',
  });
  return total;
};

export const getSubTotalMappingForCashOnCash = (status: string, headers: string[], withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[]): any => {
  const total = {};
  totalIndexArray.push(currentRowIndex);
  total[''] = `Total ${getStatusForSubTotal(status)}`;
  let columnIndex: number;
  // it's unrealized column
  const startRowIndex = currentRowIndex - dataLen;
  for (const [i, date] of headers.entries()) {
    columnIndex = i + 2;
    total[date] = getCell({
      f: `=IFERROR(SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1}), 0)`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  const indicesInHeaderDates = [];
  headers.forEach((date, index) => {
    data.forEach((company) => {
      const matchingCashFlow = company.cashFlowList.find((cashFlow) =>
        cashFlow.transactionDate.toString() === date && cashFlow.amount !== 0);
      if (matchingCashFlow) {
        indicesInHeaderDates.push(index + 2);
      }
    });
  });
  return total;
};


export const getSubTotalMappingForMoic = (status: string, headers: string[], withDue: boolean,
  withReceivables: boolean, dataLen: number, data: CashFlowMatrixDTO[]): any => {
  const total = {};
  totalIndexArray.push(currentRowIndex);
  total[''] = `Total ${getStatusForSubTotal(status)}`;
  let columnIndex: number;
  // it's unrealized column
  const stopGainLossIndexLetter = intToExcelCol(headers.length - 3);
  const startRowIndex = currentRowIndex - dataLen;
  let fmvSubTotalRowIndex: number;
  let investmentSubTotalRowIndex: number;
  if (status === 'Active') {
    fmvSubTotalRowIndex = subTotalIndexArrayFmv[0];
    investmentSubTotalRowIndex = subTotalIndexArrayInvestment[0];
  } else {
    fmvSubTotalRowIndex = subTotalIndexArrayFmv[1];
    investmentSubTotalRowIndex = subTotalIndexArrayInvestment[1];
  }
  for (const [index, date] of headers.entries()) {
    const currentColumnLetter = intToExcelCol(index + 2);
    columnIndex = index + 2;
    total[date] = getCell({
      f: `=IFERROR((${currentColumnLetter}${fmvSubTotalRowIndex}/${currentColumnLetter}${investmentSubTotalRowIndex}), 0)`,
      z: formats.emptyMultiplier,
      t: 'n',
    });
  }
  if (withDue) {
    columnIndex += 1;
    total['Due to company'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withReceivables) {
    columnIndex += 1;
    total['Receivables'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  total['Gain/Loss'] = getCell({
    f: `SUM(B${currentRowIndex}:${stopGainLossIndexLetter}${currentRowIndex})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  total['Unrealized value'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${startRowIndex}:${intToExcelCol(columnIndex)}${startRowIndex + dataLen - 1})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  const indicesInHeaderDates = [];
  headers.forEach((date, index) => {
    data.forEach((company) => {
      const matchingCashFlow = company.cashFlowList.find((cashFlow) =>
        cashFlow.transactionDate.toString() === date && cashFlow.amount !== 0);
      if (matchingCashFlow) {
        indicesInHeaderDates.push(index + 2);
      }
    });
  });
  const startIrrIndexLoop = indicesInHeaderDates[0];
  const firstNonEmptyIrr = intToExcelCol(startIrrIndexLoop);
  const dueColumnIndex = intToExcelCol(columnIndex - (withDue ? 1 : 0) - (withReceivables ? 1 : 0) - 1);
  const unrealizedValueColumnLetter = intToExcelCol(columnIndex - 1);
  const moicColumnLetter = intToExcelCol(columnIndex + 2);
  const unrValueKey = intToExcelCol(columnIndex - 1);
  const gainLossLetterKey = intToExcelCol(columnIndex);

  total['IRR'] = getCell({
    f: `IFERROR(IF(${moicColumnLetter}${currentRowIndex}=0,-1,IF(SUM(${dueColumnIndex}${currentRowIndex},${unrealizedValueColumnLetter}${currentRowIndex})<0,"",IF(${gainLossLetterKey}${currentRowIndex}>=1,XIRR(${firstNonEmptyIrr}${currentRowIndex}:${unrValueKey}${currentRowIndex},$${firstNonEmptyIrr}$1:$${unrValueKey}$1),XIRR(${firstNonEmptyIrr}${currentRowIndex}:${unrValueKey}${currentRowIndex},$${firstNonEmptyIrr}$1:$${unrValueKey}$1,-0.1)))), "")`,
    z: formats.percents,
    t: 'n',
  });

  total['MOIC'] = getCell({
    f: `IFERROR(-(${unrealizedValueColumnLetter}${currentRowIndex}) / SUMIF(${firstNonEmptyIrr}${currentRowIndex}:${dueColumnIndex}${currentRowIndex},"<"&0,${firstNonEmptyIrr}${currentRowIndex}:${dueColumnIndex}${currentRowIndex}),"")`,
    z: formats.multiplier,
    t: 'n',
  });
  return total;
};
export const getGrandTotalRow = (headers: CashOnCashHeaders, withDue: boolean, withReceivables: boolean): Rows => {
  const rows: Rows = [];
  const row = [];
  grandTotalIndexes.push(currentRowIndex);
  const grandTotalRow = getGrandTotalMapping(headers, withDue, withReceivables);
  for (const header of headers.headersForLogic) {
    row.push(withCellStyles(grandTotalRow[header], boldGrayBgCenter));
  }
  rows.push(row);
  return row;
};

export const getGrandTotalRowForCashOnCash = (headers: string[], withDue: boolean, withReceivables: boolean): Rows => {
  const rows: Rows = [];
  const row = [];
  grandTotalIndexes.push(currentRowIndex);
  const grandTotalRow = getGrandTotalMappingForCashOnCash(headers, withDue, withReceivables);
  for (const header of [''].concat(headers)) {
    row.push(withCellStyles(grandTotalRow[header], boldGrayBgCenter));
  }
  rows.push(row);
  return row;
};

export const getGrandTotalRowForMoic = (headers: string[], withDue: boolean, withReceivables: boolean): Rows => {
  const rows: Rows = [];
  const row = [];
  grandTotalIndexes.push(currentRowIndex);
  const grandTotalRow = getGrandTotalMappingForMoic(headers, withDue, withReceivables);
  for (const header of [''].concat(headers)) {
    row.push(withCellStyles(grandTotalRow[header], boldGrayBgCenter));
  }
  rows.push(row);
  return row;
};

export const getGrandTotalMapping = (headers: CashOnCashHeaders, withDue: boolean, withReceivables: boolean): any => {
  const grandTotal = {};
  grandTotal[''] = `Grand total`;
  // current column index
  let columnIndex: number;
  const letterTotalActive = totalIndexArray.length === 2 ? totalIndexArray[0] : totalIndexArray[2];
  const letterTotalRealized = totalIndexArray.length === 2 ? totalIndexArray[1] : totalIndexArray[3];
  for (const [i, date] of headers.dates.entries()) {
    columnIndex = i + 2;
    grandTotal[date] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }

  if (withDue) {
    columnIndex += 1;
    grandTotal['Due to company'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withReceivables) {
    columnIndex += 1;
    grandTotal['Receivables'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }

  const stopGainLossIndexLetter = intToExcelCol(headers.headersDisplayed.length - 3);

  grandTotal['Gain/Loss'] = getCell({
    f: `SUM(B${currentRowIndex}:${stopGainLossIndexLetter}${currentRowIndex})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  grandTotal['Due/Recievables'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  grandTotal['Unrealized value'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  const firstNonEmptyIrr = 'B';
  const unrealizedValueColumnLetter = intToExcelCol(columnIndex - 1);
  const moicColumnLetter = intToExcelCol(columnIndex + 2);
  const dueToResColumnIndex = intToExcelCol(columnIndex - 2);
  const gainLossLetterKey = intToExcelCol(columnIndex);
  const firstFlowKey = 'B';
  const irrFormula = `IFERROR(IF(${moicColumnLetter}${currentRowIndex}=0,-1,IF(SUM(${dueToResColumnIndex}${currentRowIndex},${unrealizedValueColumnLetter}${currentRowIndex})<0,"",IF(${gainLossLetterKey}${currentRowIndex}>=1,XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1),XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1,-0.1)))),"")`;

  grandTotal['IRR'] = getCell({
    f: irrFormula,
    z: formats.percents,
    t: 'n',
  });
  grandTotal['MOIC'] = getCell({
    f: `IFERROR(-(${unrealizedValueColumnLetter}${currentRowIndex})/SUMIF(${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex},"<"&0,${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex}),"")`,
    z: formats.multiplier,
    t: 'n',
  });
  return grandTotal;
};

export const getGrandTotalMappingForCashOnCash = (headers: string[], withDue: boolean, withReceivables: boolean): any => {
  const grandTotal = {};
  grandTotal[''] = `Grand total`;
  // current column index
  let columnIndex: number;
  const letterTotalActive = totalIndexArray.length === 2 ? totalIndexArray[0] : totalIndexArray[2];
  const letterTotalRealized = totalIndexArray.length === 2 ? totalIndexArray[1] : totalIndexArray[3];
  for (const [i, date] of headers.entries()) {
    columnIndex = i + 2;
    grandTotal[date] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }

  if (withDue) {
    columnIndex += 1;
    grandTotal['Due to company'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withReceivables) {
    columnIndex += 1;
    grandTotal['Receivables'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }

  const stopGainLossIndexLetter = intToExcelCol(headers.length - 3);

  grandTotal['Gain/Loss'] = getCell({
    f: `SUM(B${currentRowIndex}:${stopGainLossIndexLetter}${currentRowIndex})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  grandTotal['Due/Recievables'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  grandTotal['Unrealized value'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  const firstNonEmptyIrr = 'B';
  const unrealizedValueColumnLetter = intToExcelCol(columnIndex - 1);
  const moicColumnLetter = intToExcelCol(columnIndex + 2);
  const dueToResColumnIndex = intToExcelCol(columnIndex - 2);
  const gainLossLetterKey = intToExcelCol(columnIndex);
  const firstFlowKey = 'B';
  const irrFormula = `IFERROR(IF(${moicColumnLetter}${currentRowIndex}=0,-1,IF(SUM(${dueToResColumnIndex}${currentRowIndex},${unrealizedValueColumnLetter}${currentRowIndex})<0,"",IF(${gainLossLetterKey}${currentRowIndex}>=1,XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1),XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1,-0.1)))),"")`;

  grandTotal['IRR'] = getCell({
    f: irrFormula,
    z: formats.percents,
    t: 'n',
  });
  grandTotal['MOIC'] = getCell({
    f: `IFERROR(-(${unrealizedValueColumnLetter}${currentRowIndex})/SUMIF(${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex},"<"&0,${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex}),"")`,
    z: formats.multiplier,
    t: 'n',
  });
  return grandTotal;
};

export const getGrandTotalMappingForMoic = (headers: string[], withDue: boolean, withReceivables: boolean): any => {
  const grandTotal = {};
  grandTotal[''] = `Grand total`;
  // current column index
  let columnIndex: number;
  const fmvGrandTotalRow = grandTotalIndexes[1];
  const investmentGrandTotalRow = grandTotalIndexes[0];
  const letterTotalActive = totalIndexArray.length === 2 ? totalIndexArray[0] : totalIndexArray[2];
  const letterTotalRealized = totalIndexArray.length === 2 ? totalIndexArray[1] : totalIndexArray[3];
  for (const [index, date] of headers.entries()) {
    const currentColumnLetter = intToExcelCol(index + 2);
    columnIndex = index + 2;
    grandTotal[date] = getCell({
      f: `=IFERROR(${currentColumnLetter}${fmvGrandTotalRow}/${currentColumnLetter}${investmentGrandTotalRow},0)`,
      z: formats.emptyMultiplier,
      t: 'n',
    });
  }

  if (withDue) {
    columnIndex += 1;
    grandTotal['Due to company'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }
  if (withReceivables) {
    columnIndex += 1;
    grandTotal['Receivables'] = getCell({
      f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
      z: formats.thousandsSeparator,
      t: 'n',
    });
  }

  const stopGainLossIndexLetter = intToExcelCol(headers.length - 3);
  grandTotal['Gain/Loss'] = getCell({
    f: `SUM(B${currentRowIndex}:${stopGainLossIndexLetter}${currentRowIndex})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  grandTotal['Due/Recievables'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  grandTotal['Unrealized value'] = getCell({
    f: `=SUM(${intToExcelCol(columnIndex)}${letterTotalActive},${intToExcelCol(columnIndex)}${letterTotalRealized})`,
    z: formats.thousandsSeparator,
    t: 'n',
  });
  columnIndex += 1;
  const firstNonEmptyIrr = 'B';
  const unrealizedValueColumnLetter = intToExcelCol(columnIndex - 1);
  const moicColumnLetter = intToExcelCol(columnIndex + 2);
  const dueToResColumnIndex = intToExcelCol(columnIndex - 2);
  const gainLossLetterKey = intToExcelCol(columnIndex);
  const firstFlowKey = 'B';
  const irrFormula = `IFERROR(IF(${moicColumnLetter}${currentRowIndex}=0,-1,IF(SUM(${dueToResColumnIndex}${currentRowIndex},${unrealizedValueColumnLetter}${currentRowIndex})<0,"",IF(${gainLossLetterKey}${currentRowIndex}>=1,XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1),XIRR(B${currentRowIndex}:${unrealizedValueColumnLetter}${currentRowIndex},$${firstFlowKey}$1:$${unrealizedValueColumnLetter}$1,-0.1)))),"")`;

  grandTotal['IRR'] = getCell({
    f: irrFormula,
    z: formats.percents,
    t: 'n',
  });
  grandTotal['MOIC'] = getCell({
    f: `IFERROR(-(${unrealizedValueColumnLetter}${currentRowIndex})/SUMIF(${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex},"<"&0,${firstNonEmptyIrr}${currentRowIndex}:${dueToResColumnIndex}${currentRowIndex}),"")`,
    z: formats.multiplier,
    t: 'n',
  });
  return grandTotal;
};

const refreshUtilsData = (): void => {
  currentRowIndex = 2;
  investmentRowValueIndexes = [];
  fmvRowValueIndexes = [];
  subTotalIndexArrayInvestment = [];
  subTotalIndexArrayFmv = [];
  grandTotalIndexes = [];
  totalIndexArray = [];
  firstValueArray = [];
};

// row index for checking current row
let currentRowIndex = 2;
let rowIterator = 0;
let investmentRowValueIndexes: number[] = [];
let fmvRowValueIndexes: number[] = [];
let subTotalIndexArrayInvestment: number[] = [];
let subTotalIndexArrayFmv: number[] = [];
let grandTotalIndexes: number[] = [];

let totalIndexArray = [];
// array to define first cashflow amount value for define irr formulas
let firstValueArray = [];

export const boldLightGrayBgCenter: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'e3e3e3' } },
  alignment: { horizontal: 'center' },
};

export const boldGrayBgCenter: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'cacaca' } },
  alignment: { horizontal: 'center' },
};

export const boldGrayBgLeft: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'cacaca' } },
  alignment: { horizontal: 'left' },
};
